// Geometry for the Jacques chart.
//
// Source of truth is the DOMAIN model: curves and the candidate trace are
// expressed as { age, level } points, independent of any pixel mapping. The
// pixel layer (getCoords) is derived on top, so the render can later move to a
// coordinate/scale model without changing the domain geometry.
//
// Equivalence with the legacy canvas (src/Jacques.js drawLines/drawLine/
// drawMidLine) is proven in jacquesGeometry.test.js against a golden master.
// The legacy code is left untouched; we only reuse the exported `getCoords`.
import { getCoords } from '../Jacques';

// 8 main Jacques curves (green). Values are capacity level at ages 20,25,..,65.
export const JACQUES_LINES = [
    [0, 0.8, 1.55, 2.15, 2.8, 3.25, 3.65, 3.8, 3.85, 3.8],
    [1.1, 2.3, 3.5, 4.6, 5.5, 6.0, 6.5, 6.75, 6.85, 6.8],
    [2.9, 4.5, 5.9, 7.1, 7.9, 8.5, 9.0, 9.4, 9.7, 9.7],
    [4.8, 6.4, 7.75, 8.75, 9.7, 10.4, 11.2, 11.8, 12.2, 12.6],
    [6.3, 7.8, 9.1, 10.2, 11.2, 12.3, 13.3, 14.3, 15, 15.7],
    [7.6, 9.2, 10.7, 11.9, 13.1, 14.3, 15.5, 16.7, 17.8, 18.6],
    [8.7, 10.6, 12.1, 13.6, 15.0, 16.35, 17.7, 19, 20.2, 21.1],
    [10, 12.2, 14, 15.7, 17.3, 18.8, 20.3, 21.5, 22.5, 23.5],
];

const FIRST_AGE = 20;
const AGE_STEP = 5;

// ─── Domain geometry (no pixels) ────────────────────────────────────────────

// The 8 green Jacques curves as { age, level } point arrays.
export function jacquesCurvesDomain() {
    return JACQUES_LINES.map((stops) =>
        stops.map((level, i) => ({ age: FIRST_AGE + AGE_STEP * i, level })));
}

// Interpolated stops between two lines, ported from drawMidLines.
function interpStops(la, lb, linesTotal, idx) {
    return la.map((a, j) => (lb[j] - a) / (linesTotal + 1) * idx + a);
}

const L = JACQUES_LINES;

// Maps a curve index (tuCurva, 1..22) to its stop array, mirroring the switch
// in drawLines. Returns null for indices with no candidate line.
function candidateStops(curva) {
    switch (curva) {
        case 1: return L[0];
        case 2: return interpStops(L[0], L[1], 2, 1);
        case 3: return interpStops(L[0], L[1], 2, 2);
        case 4: return L[1];
        case 5: return interpStops(L[1], L[2], 3, 1);
        case 6: return interpStops(L[1], L[2], 3, 2);
        case 7: return interpStops(L[1], L[2], 3, 3);
        case 8: return L[2];
        case 9: return interpStops(L[2], L[3], 3, 1);
        case 10: return interpStops(L[2], L[3], 3, 2);
        case 11: return interpStops(L[2], L[3], 3, 3);
        case 12: return L[3];
        case 13: return interpStops(L[3], L[4], 3, 1);
        case 14: return interpStops(L[3], L[4], 3, 2);
        case 15: return interpStops(L[3], L[4], 3, 3);
        case 16: return L[4];
        case 17: return interpStops(L[4], L[5], 2, 1);
        case 18: return interpStops(L[4], L[5], 2, 2);
        case 19: return L[5];
        case 20: return interpStops(L[5], L[6], 2, 1);
        case 21: return L[6];
        case 22: return interpStops(L[6], L[7], 2, 1);
        default: return null;
    }
}

// curva === 0 is a fixed low blue curve drawn by the canvas in raw pixels; it
// has no natural domain representation (its points are Bézier controls), so we
// keep it as a fixed pixel path.
const CURVA_0_CANDIDATE_PATH = 'M90,480 Q170,460 250,445 Q330,425 479,440';

// Level on a stops curve at an arbitrary age, interpolating between the 5-year
// table entries. The indexing age is clamped to the table domain [20, 65]:
// below 20 the floor() would index stops[-1] (undefined -> NaN path), above 65
// stops[>last]; an out-of-range age reads flat at the nearest table value.
function levelAtAge(stops, age) {
    const idxAge = Math.max(FIRST_AGE, Math.min(age, 65));
    const xVacio = idxAge - FIRST_AGE;
    const posEdad = Math.floor(xVacio / AGE_STEP);
    const extraEdad = xVacio % AGE_STEP;
    return age < 65
        ? stops[posEdad] + (stops[posEdad + 1] - stops[posEdad]) * extraEdad / AGE_STEP
        : stops[posEdad];
}

// Candidate trace + dot for a curve index and age, in DOMAIN coords.
// Ported from drawMidLine(stops, age). For age >= 65 the line is omitted (only
// the dot is shown), matching the legacy behaviour.
export function candidateCurveDomain(curva, age) {
    if (curva === 0) return { points: [], dot: null, fixedPixelPath: CURVA_0_CANDIDATE_PATH };
    const stops = candidateStops(curva);
    if (!stops) return { points: [], dot: null };

    const posEdad = Math.floor((Math.max(FIRST_AGE, Math.min(age, 65)) - FIRST_AGE) / AGE_STEP);
    const startLevel = levelAtAge(stops, age);

    const dot = { age, level: startLevel };
    if (age >= 65) return { points: [], dot };

    const points = [{ age, level: startLevel }];
    for (let i = posEdad + 1; i < stops.length; i++) {
        points.push({ age: FIRST_AGE + AGE_STEP * i, level: stops[i] });
    }
    return { points, dot };
}

// Segment of the candidate curve already traversed — from the age at reply time
// up to the current age — in DOMAIN coords. Empty when the range is degenerate
// (same age, missing values) or the curva has no stops line (incl. curva 0,
// whose candidate path is a fixed pixel curve independent of age).
export function candidateHistoryDomain(curva, fromAge, toAge) {
    if (fromAge == null || toAge == null || toAge <= fromAge) return { points: [] };
    const stops = candidateStops(curva);
    if (!stops) return { points: [] };

    const points = [{ age: fromAge, level: levelAtAge(stops, fromAge) }];
    for (let i = 0; i < stops.length; i++) {
        const stopAge = FIRST_AGE + AGE_STEP * i;
        if (stopAge > fromAge && stopAge < toAge) points.push({ age: stopAge, level: stops[i] });
    }
    points.push({ age: toAge, level: levelAtAge(stops, toAge) });
    return { points };
}

// ─── Render layer (projection: domain -> screen) ────────────────────────────
//
// `project` maps a domain point { age, level } to screen { x, y }. Pass
// getCoords-based projection to reproduce the legacy pixel chart, or a
// scaleLinear-based projection for a responsive, modern layout.

export const getCoordsProjection = (p) => getCoords(p.age, p.level);

// Smooth quadratic command sequence through screen points, replicating the
// canvas drawLine / drawMidLine logic exactly. Because the projection is
// affine, smoothing in screen space matches the legacy curve shape.
export function smoothCommands(points) {
    if (!points.length) return [];
    const cmds = [['moveTo', points[0].x, points[0].y]];
    if (points.length >= 3) {
        let i;
        for (i = 1; i < points.length - 2; i++) {
            const xc = (points[i].x + points[i + 1].x) / 2;
            const yc = (points[i].y + points[i + 1].y) / 2;
            cmds.push(['quadraticCurveTo', points[i].x, points[i].y, xc, yc]);
        }
        cmds.push(['quadraticCurveTo', points[i].x, points[i].y, points[i + 1].x, points[i + 1].y]);
    } else if (points.length === 2) {
        cmds.push(['quadraticCurveTo', points[0].x, points[0].y, points[1].x, points[1].y]);
    }
    return cmds;
}

function commandsToPath(cmds) {
    return cmds
        .map((c) => (c[0] === 'moveTo'
            ? `M${c[1]},${c[2]}`
            : `Q${c[1]},${c[2]} ${c[3]},${c[4]}`))
        .join(' ');
}

function buildSmoothPath(domainPoints, project) {
    if (!domainPoints.length) return '';
    return commandsToPath(smoothCommands(domainPoints.map(project)));
}

// Curva 0 curves were drawn by the canvas in raw pixels; ported back to domain
// by inverting getCoords (age = (x+111)/9.33, level = (480-y)/20) so they can
// be re-projected with any scale. Projecting these with getCoords reproduces
// the original pixel paths exactly (asserted in jacquesGeometry.test.js).
const CURVA_0_GREEN = {
    move: { age: 28.1886, level: -0.25 },
    quads: [{ c: { age: 41.8006, level: 1.0 }, to: { age: 64.9518, level: 0.5 } }],
};
const CANDIDATE_0 = {
    move: { age: 21.5434, level: 0 },
    quads: [
        { c: { age: 30.1179, level: 1.0 }, to: { age: 38.6924, level: 1.75 } },
        { c: { age: 47.2669, level: 2.75 }, to: { age: 63.2369, level: 2.0 } },
    ],
};

function projectQuadSeq(seq, project) {
    const m = project(seq.move);
    let d = `M${m.x},${m.y}`;
    for (const q of seq.quads) {
        const c = project(q.c);
        const to = project(q.to);
        d += ` Q${c.x},${c.y} ${to.x},${to.y}`;
    }
    return d;
}

// Every curve of the model (indices 0..22 — the 8 main Jacques lines plus the
// interpolated ones between them), drawn full length from age 20, as projected
// paths. `main` flags the curves that ARE one of the green reference lines, so a
// caller drawing the whole family on top of the reference layer can skip them
// and avoid a double stroke.
// `end` is the projected last point of each curve, so a caller can label it, and
// `endLevel` is that point's domain level — the level the curve ACTUALLY
// reaches as drawn. Don't label these off Jacques.js's `curvas` table instead:
// that table only goes up to 6A, so the top curves would all be labelled alike.
export function getAllCurvePaths(project) {
    const zeroQuads = CANDIDATE_0.quads;
    const paths = [{
        curva: 0,
        main: false,
        path: projectQuadSeq(CANDIDATE_0, project),
        end: project(zeroQuads[zeroQuads.length - 1].to),
    }];
    for (let curva = 1; curva <= 22; curva++) {
        const stops = candidateStops(curva);
        if (!stops) continue;
        const domain = stops.map((level, i) => ({ age: FIRST_AGE + AGE_STEP * i, level }));
        paths.push({
            curva,
            main: JACQUES_LINES.includes(stops),
            path: buildSmoothPath(domain, project),
            end: project(domain[domain.length - 1]),
            endLevel: domain[domain.length - 1].level,
        });
    }
    return paths;
}

// Reference curves (the 8 green Jacques lines + curva0) as projected paths.
export function getReferenceCurvePaths(project) {
    return {
        jacques: jacquesCurvesDomain().map((curve) => buildSmoothPath(curve, project)),
        curva0: projectQuadSeq(CURVA_0_GREEN, project),
    };
}

// Candidate trace + dot, projected with the given scale. `points` are the
// projected screen points (for building the gradient area under the line).
export function getCandidate(curva, age, project) {
    if (curva === 0) return { path: projectQuadSeq(CANDIDATE_0, project), dot: null, points: [] };
    const dom = candidateCurveDomain(curva, age);
    const dot = dom.dot ? project(dom.dot) : null;
    if (!dom.points.length) return { path: '', dot, points: [] };
    const points = dom.points.map(project);
    return { path: buildSmoothPath(dom.points, project), dot, points };
}

// Traversed segment (age at reply -> current age), projected. `start` is the
// projected point at the reply age, for marking where the test was taken.
export function getCandidateHistory(curva, fromAge, toAge, project) {
    const dom = candidateHistoryDomain(curva, fromAge, toAge);
    if (!dom.points.length) return { path: '', start: null };
    return { path: buildSmoothPath(dom.points, project), start: project(dom.points[0]) };
}

// ─── Y-axis sublevel labels ──────────────────────────────────────────────────

// Bottom -> top (roman numerals as drawn in the PNG).
const SUBLEVEL_LABELS = [
    'ID', 'IC', 'IB', 'IA',
    'IIC', 'IIB', 'IIA',
    'IIIC', 'IIIB', 'IIIA',
    'IVC', 'IVB', 'IVA',
    'VC', 'VB', 'VA',
    'VIC', 'VIB', 'VIA',
    'VIIC', 'VIIB', 'VIIA',
];

// Sublevel-to-level mapping, measured from the legacy PNG grid: rows are 20px
// apart and getCoords maps 20px to exactly 1 level unit, so each sublevel is 1
// level unit. Row centres: ID ≈ level 0.15, VIIA ≈ level 21.15.
const BASE_LEVEL = 0.15;
export const LEVEL_STEP = 1.0;

export function getSublevelTicks() {
    return SUBLEVEL_LABELS.map((label, i) => ({
        label,
        level: BASE_LEVEL + i * LEVEL_STEP,
    }));
}

// Inverse of sublevelToDomainLevel: the drawn sublevel row a domain level falls
// on ('VIIA'), or '' if there is none. Levels above the top row (the highest
// curves run past VIIA and get clipped) read as the top row, which is the
// highest thing the grid can name.
export function domainLevelToSublevel(level) {
    if (!Number.isFinite(level)) return '';
    const i = Math.round((level - BASE_LEVEL) / LEVEL_STEP);
    if (i < 0) return '';
    return SUBLEVEL_LABELS[Math.min(i, SUBLEVEL_LABELS.length - 1)];
}

const ROMAN_BY_LEVEL = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

// Maps a discrete capacity level+sublevel (e.g. 2, 'A') to its domain level on
// the chart grid, so dashboard dots land on the drawn sublevel rows. Use this
// for positioning instead of mapToMlLevel: mapToMlLevel adds +1 for level 1
// (and predates the 'ID' row), which lifts every level-1 dot a full row and
// collides 1A with 2C. Returns null for an unknown level/sublevel.
export function sublevelToDomainLevel(level, sublevel) {
    const roman = ROMAN_BY_LEVEL[level];
    if (!roman || !sublevel) return null;
    const i = SUBLEVEL_LABELS.indexOf(`${roman}${String(sublevel).toUpperCase()}`);
    return i < 0 ? null : BASE_LEVEL + i * LEVEL_STEP;
}

// ─── Role groups ─────────────────────────────────────────────────────────────
//
// Each roman level (I..VII) maps to a role group. This mapping was previously
// baked into the legacy PNG (jacques-no-lines-*.png) and had no representation
// in code; it is reconstructed here. Bottom (I) -> top (VII).
const ROLE_GROUP_NAMES = {
    I: 'Operador',
    II: 'Coordinador / Analista',
    III: 'Senior Manager',
    IV: 'Senior Director',
    V: 'President / VP',
    VI: 'C-Level',
    VII: 'CEO Global',
};

// Groups the sublevel ticks by roman level into role-group bands, each with its
// level extents (± half a unit so bands tile the plot) and constituent ticks.
// Returned bottom -> top, matching getSublevelTicks order.
export function getRoleGroupBands() {
    const order = [];
    const groups = {};
    getSublevelTicks().forEach((tick) => {
        const roman = tick.label.replace(/[A-D]$/, '');
        if (!groups[roman]) {
            groups[roman] = [];
            order.push(roman);
        }
        groups[roman].push(tick);
    });
    return order.map((roman) => {
        const levels = groups[roman].map((tk) => tk.level);
        const top = Math.max(...levels) + 0.5;
        const bottom = Math.min(...levels) - 0.5;
        return {
            roman,
            name: ROLE_GROUP_NAMES[roman] || roman,
            ticks: groups[roman],
            levelTop: top,
            levelBottom: bottom,
            levelMid: (top + bottom) / 2,
        };
    });
}

// ─── Y-axis role-group labels per company ────────────────────────────────────
//
// The left-axis role names were previously baked, per company and per language,
// into the legacy PNGs (jacques-no-lines-<lang>.png and the per-client ternium/
// and marval/ variants — see utils/charts.js). With the visx chart the names
// are real text, so we reconstruct that mapping here.
//
// Each company maps every sublevel (SUBLEVEL_LABELS) to a translation KEY; the
// es/pt/en strings live in components/common/Texts.js and are resolved by the
// caller with `tr` (withLanguage). Consecutive sublevels carrying the same key
// are merged into a single band centred vertically over the run — so a role that
// spans three sublevels is simply written three times and renders once, while a
// client (e.g. marval) can name each sublevel individually.
//
// Selection mirrors getChart: an unknown company falls back to `all`. The
// structural bands/tints (getRoleGroupBands) stay per-roman and are unaffected.
export const ROLE_GROUP_LABELS = {
    all: {
        VIIA: 'role_ceo_global', VIIB: 'role_ceo_global', VIIC: 'role_ceo_global',
        VIA: 'role_c_level', VIB: 'role_c_level', VIC: 'role_c_level',
        VA: 'role_president', VB: 'role_president', VC: 'role_vp',
        IVA: 'role_senior_director', IVB: 'role_senior_director', IVC: 'role_director',
        IIIA: 'role_senior_manager', IIIB: 'role_senior_manager', IIIC: 'role_manager',
        IIA: 'role_coordinator', IIB: 'role_analyst', IIC: 'role_analyst',
        IA: 'role_operator', IB: 'role_operator', IC: 'role_operator', ID: 'role_operator',
    },
    ternium: {
        VIIA: 'role_ceo_global_tx', VIIB: 'role_ceo_global_tx', VIIC: 'role_ceo_global_tx',
        VIA: 'role_c_level_president', VIB: 'role_c_level_president', VIC: 'role_c_level_president',
        VA: 'role_c_level_functional', VB: 'role_c_level_functional', VC: 'role_vp',
        IVA: 'role_senior_director', IVB: 'role_senior_director', IVC: 'role_director',
        IIIA: 'role_senior_manager', IIIB: 'role_senior_manager', IIIC: 'role_manager',
        IIA: 'role_coordinator', IIB: 'role_analyst', IIC: 'role_analyst',
        IA: 'role_operator', IB: 'role_operator', IC: 'role_operator', ID: 'role_operator',
    },
    marval: {
        VIIA: 'role_ceo_global', VIIB: 'role_ceo_global', VIIC: 'role_ceo_global',
        VIA: 'role_c_level', VIB: 'role_c_level', VIC: 'role_c_level',
        VA: 'role_chairman', VB: 'role_managing_partner', VC: 'role_managing_partner',
        IVA: 'role_partner', IVB: 'role_partner', IVC: 'role_partner',
        IIIA: 'role_partner_rn', IIIB: 'role_senior_expert_advanced', IIIC: 'role_senior_rn',
        IIA: 'role_semi_senior', IIB: 'role_junior_advanced', IIC: 'role_junior_advanced',
        IA: 'role_junior_professional', IB: 'role_paralegal', IC: 'role_attorney', ID: 'role_intern',
    },
};

// Groups a company's per-sublevel keys into vertically-centred bands by merging
// consecutive sublevels (SUBLEVEL_LABELS, bottom -> top) that share a key. Each
// band carries its translation `key` plus placement; the caller resolves the key
// to text via `tr`. Unknown company -> 'all'.
export function getRoleGroupLabels(company) {
    const map = ROLE_GROUP_LABELS[company] || ROLE_GROUP_LABELS.all;
    const bands = [];
    let run = null; // { key, loIdx, hiIdx }
    SUBLEVEL_LABELS.forEach((sub, idx) => {
        const key = map[sub];
        if (run && key === run.key) {
            run.hiIdx = idx;
        } else {
            if (run) bands.push(run);
            run = key ? { key, loIdx: idx, hiIdx: idx } : null;
        }
    });
    if (run) bands.push(run);

    // Each sublevel cell spans [level_i, level_i + LEVEL_STEP] (the grid rows sit
    // on level_i; the sublevel label sits half a step above, at the cell centre).
    // A band over sublevels lo..hi therefore occupies [level_lo, level_hi + STEP];
    // its centre is half a step above the (lo+hi)/2 midpoint — matching how the
    // roman sublevel labels are placed, so single-sublevel bands line up exactly.
    return bands.map((b) => ({
        key: b.key,
        levelBottom: BASE_LEVEL + b.loIdx * LEVEL_STEP,
        levelTop: BASE_LEVEL + (b.hiIdx + 1) * LEVEL_STEP,
        levelMid: BASE_LEVEL + ((b.loIdx + b.hiIdx) / 2 + 0.5) * LEVEL_STEP,
        sublevelCount: b.hiIdx - b.loIdx + 1,
    }));
}
